import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PortalLayout from '@/components/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api, formatApiError } from '@/App';
import PdfPreviewDialog from '@/components/PdfPreviewDialog';
import { FileText, Download, CheckCircle, XCircle, ArrowLeft, Warning, ShieldCheck, Eye } from '@phosphor-icons/react';

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-700',
  accepted: 'bg-blue-100 text-blue-700',
  declined: 'bg-red-100 text-red-700',
  signed: 'bg-green-100 text-green-700',
};

// Steps: terms → quotation → accepted
const QuotationDetail = () => {
  const { quotationId } = useParams();
  const navigate = useNavigate();
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('terms');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [acceptingTerms, setAcceptingTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    const fetchQuotation = async () => {
      try {
        const { data } = await api.get(`/quotations/${quotationId}`);
        setQuotation(data);
        if (data.status === 'signed') setStep('done');
        else if (data.status === 'declined') setStep('done');
        else if (data.status === 'accepted') setStep('confirm');
        else if (data.terms_accepted) setStep('quotation');
        else setStep('terms');
      } catch (e) { toast.error('Failed to load quotation'); navigate('/portal'); }
      finally { setLoading(false); }
    };
    fetchQuotation();
  }, [quotationId, navigate]);

  const handleAcceptTerms = async () => {
    if (!termsAccepted) { toast.error('Please check the checkbox to accept terms'); return; }
    setAcceptingTerms(true);
    try {
      await api.post(`/quotations/${quotationId}/accept-terms`);
      setQuotation(prev => ({ ...prev, terms_accepted: true }));
      setStep('quotation');
      toast.success('Terms accepted. You can now review the quotation.');
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAcceptingTerms(false); }
  };

  const handleRespond = async (action) => {
    try {
      await api.post(`/quotations/${quotationId}/respond?action=${action}`);
      if (action === 'accept') {
        setQuotation(prev => ({ ...prev, status: 'accepted' }));
        setStep('confirm');
        toast.success('Quotation accepted!');
      } else {
        setQuotation(prev => ({ ...prev, status: 'declined' }));
        setStep('done');
        toast.success('Quotation declined.');
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleConfirmAndSendPdf = async () => {
    setSubmitting(true);
    try {
      await api.post(`/quotations/${quotationId}/sign`);
      toast.success('Terms of Service sent to your email!');
      setQuotation(prev => ({ ...prev, status: 'signed' }));
      setStep('done');
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  if (loading) return <PortalLayout><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-[#4CAF50] border-t-transparent rounded-full animate-spin"></div></div></PortalLayout>;
  if (!quotation) return null;

  const steps = [
    { key: 'terms', label: 'Terms & Conditions' },
    { key: 'quotation', label: 'Review Quotation' },
    { key: 'confirm', label: 'Confirm & Receive PDF' },
  ];
  const currentStepIdx = step === 'done' ? 3 : steps.findIndex(s => s.key === step);

  return (
    <PortalLayout>
      <div className="max-w-4xl mx-auto space-y-6 fade-in">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/portal/quotations')} className="rounded-none" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <h1 className="text-2xl font-bold text-[#0A192F]">Quotation</h1>
            <p className="text-sm text-gray-500">{quotation.quotation_id}</p>
          </div>
          <Badge className={STATUS_COLORS[quotation.status]}>{quotation.status}</Badge>
        </div>

        {/* Progress Steps */}
        {quotation.status !== 'declined' && (
          <div className="flex items-center gap-2" data-testid="progress-steps">
            {steps.map((s, i) => (
              <React.Fragment key={s.key}>
                <div className={`flex items-center gap-2 px-3 py-2 text-xs font-medium ${
                  i < currentStepIdx ? 'bg-[#4CAF50]/10 text-[#4CAF50]' :
                  i === currentStepIdx ? 'bg-[#4CAF50] text-white' :
                  'bg-gray-100 text-gray-400'
                }`}>
                  {i < currentStepIdx ? <CheckCircle className="w-4 h-4" weight="fill" /> : <span>{i + 1}</span>}
                  <span className="hidden sm:inline">{s.label}</span>
                </div>
                {i < steps.length - 1 && <div className={`flex-1 h-0.5 ${i < currentStepIdx ? 'bg-[#4CAF50]' : 'bg-gray-200'}`} />}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* STEP 1: Terms */}
        {step === 'terms' && (
          <Card>
            <CardHeader className="border-b pb-4">
              <CardTitle className="flex items-center gap-2 text-lg"><ShieldCheck className="w-5 h-5 text-[#4CAF50]" /> Service Terms & Conditions</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-600 mb-4">Please review the terms and conditions below before proceeding.</p>
              <div className="bg-gray-50 border border-gray-200 p-6 max-h-[400px] overflow-y-auto text-sm text-gray-700 leading-relaxed mb-6" data-testid="terms-content">
                <h3 className="font-bold text-base mb-3">MAREMA CONSULTING SERVICE AGREEMENT</h3>
                <p className="mb-3">This Service Agreement is entered into between Marema Consulting (Pty) Ltd ("the Consultant") and the Client.</p>
                <p className="mb-2"><strong>1. Scope of Services:</strong> The Consultant agrees to provide the services as described in the accepted quotation. The Consultant will act on behalf of the Client in all dealings with the relevant municipality or energy provider.</p>
                <p className="mb-2"><strong>2. Authority:</strong> The Client authorises Marema Consulting to access, review, and manage their municipal accounts as required to deliver the agreed services.</p>
                <p className="mb-2"><strong>3. Confidentiality:</strong> All information shared between the parties is confidential and will be handled in accordance with the Protection of Personal Information Act (POPIA).</p>
                <p className="mb-2"><strong>4. Fees and Payment:</strong> The fixed component price is R10,000 (excluding VAT). Recovery of any overbilled amounts from the council will be charged at 25% of any credit reversals or interim payments. Late payment may result in suspension of services.</p>
                <p className="mb-2"><strong>5. Duration:</strong> This agreement shall commence upon signing. Service turnaround time is 30-90 days depending on service type and municipality. Either party may terminate with 30 days written notice.</p>
                <p className="mb-2"><strong>6. Limitation of Liability:</strong> The Consultant shall not be liable for indirect, consequential, or incidental damages. All services are provided in good faith.</p>
                <p className="mb-2"><strong>7. Governing Law:</strong> This agreement is governed by the laws of the Republic of South Africa.</p>
              </div>
              <div className="flex items-center gap-3 mb-6">
                <input type="checkbox" id="accept-terms" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} className="w-4 h-4 accent-[#4CAF50]" data-testid="accept-terms-checkbox" />
                <label htmlFor="accept-terms" className="text-sm text-gray-700">I have read and agree to the Terms and Conditions</label>
              </div>
              <Button onClick={handleAcceptTerms} disabled={!termsAccepted || acceptingTerms} className="bg-[#4CAF50] hover:bg-[#45a049] text-white rounded-none" data-testid="accept-terms-btn">
                {acceptingTerms ? 'Processing...' : 'Accept Terms & View Quotation'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* STEP 2: Review Quotation */}
        {step === 'quotation' && (
          <>
            <Card>
              <CardHeader className="border-b pb-4">
                <CardTitle className="flex items-center gap-2 text-lg"><FileText className="w-5 h-5 text-[#4CAF50]" /> Quotation Details</CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><p className="text-sm text-gray-500">Company</p><p className="font-medium">{quotation.company?.company_name || 'N/A'}</p></div>
                  <div><p className="text-sm text-gray-500">Description</p><p className="font-medium">{quotation.description || 'N/A'}</p></div>
                  {quotation.amount && <div><p className="text-sm text-gray-500">Amount (incl. VAT)</p><p className="font-medium text-lg">R {quotation.amount?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</p></div>}
                  <div><p className="text-sm text-gray-500">Date</p><p className="font-medium">{new Date(quotation.created_at).toLocaleDateString()}</p></div>
                </div>
                <div className="pt-4 flex flex-wrap gap-3">
                  <Button
                    variant="outline"
                    className="rounded-none"
                    onClick={() => setPreviewOpen(true)}
                    data-testid="view-quotation-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" /> View Quotation PDF
                  </Button>
                  <a href={quotation.source === 'generated' ? `/api/quotations/${quotation.quotation_id}/pdf?download=true` : `/api/quotations/${quotation.quotation_id}/download`} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="rounded-none" data-testid="download-quotation-btn"><Download className="w-4 h-4 mr-2" /> Download PDF</Button>
                  </a>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-[#0A192F] mb-4">Your Response</h3>
                <p className="text-gray-600 mb-6">Review the quotation above and accept or decline.</p>
                <div className="flex gap-4">
                  <Button onClick={() => handleRespond('accept')} className="bg-[#4CAF50] hover:bg-[#45a049] text-white rounded-none" data-testid="accept-quotation-btn"><CheckCircle className="w-4 h-4 mr-2" /> Accept Quotation</Button>
                  <Button variant="outline" onClick={() => handleRespond('decline')} className="rounded-none text-red-600 border-red-300 hover:bg-red-50" data-testid="decline-quotation-btn"><XCircle className="w-4 h-4 mr-2" /> Decline</Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* STEP 3: Confirm & receive PDF */}
        {step === 'confirm' && (
          <Card>
            <CardHeader className="border-b pb-4">
              <CardTitle className="flex items-center gap-2 text-lg"><ShieldCheck className="w-5 h-5 text-[#4CAF50]" /> Confirm Acceptance</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-gray-600 mb-6">
                You have accepted the quotation. Click below to confirm and receive the Terms of Service document via email.
              </p>
              <Button onClick={handleConfirmAndSendPdf} disabled={submitting} className="bg-[#4CAF50] hover:bg-[#45a049] text-white rounded-none" data-testid="confirm-send-pdf-btn">
                {submitting ? 'Sending...' : 'Confirm & Send Terms of Service PDF'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* DONE states */}
        {step === 'done' && quotation.status === 'signed' && (
          <Card><CardContent className="p-8 text-center"><CheckCircle className="w-16 h-16 text-[#4CAF50] mx-auto mb-4" weight="fill" /><h3 className="text-xl font-bold text-[#0A192F] mb-2">Terms Accepted</h3><p className="text-gray-600">Your Terms of Service document has been emailed to you.</p></CardContent></Card>
        )}
        {step === 'done' && quotation.status === 'declined' && (
          <Card><CardContent className="p-8 text-center"><Warning className="w-16 h-16 text-orange-400 mx-auto mb-4" weight="fill" /><h3 className="text-xl font-bold text-[#0A192F] mb-2">Quotation Declined</h3><p className="text-gray-600">You have declined this quotation. Please contact us if you have any questions.</p></CardContent></Card>
        )}

        <PdfPreviewDialog
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          url={quotation.source === 'generated' ? `/api/quotations/${quotation.quotation_id}/pdf` : `/api/quotations/${quotation.quotation_id}/download`}
          title="Quotation"
          filename={quotation.original_filename}
        />
      </div>
    </PortalLayout>
  );
};

export default QuotationDetail;
